import { Roller } from "@fecapark/number-rolling";
import { useDisclosure } from "@heroui/react";
import { useCurrentAccount, useWalletConnection } from "@mysten/dapp-kit-react";
import type { LeaseDailyRewardPoint } from "@swarm/shared/schemas";
import { CalculateLeasingRewardsResponse } from "@swarm/shared/schemas";
import { LeaseRecord } from "@swarm/shared/schemas";
import clsx from "clsx";
import React, { useEffect, useState } from "react";
import { z } from "zod";

import { QuestionMarkCircledIcon } from "@/components/icons";
import { useAuthenticationContext } from "@/context/authentication/useAuthenticationContext";
import { useClaimsContext } from "@/context/claims/useClaimsContext";
import { RequestNewLeaseFormSchema } from "@/context/license-leases/LicenseLeasesContext.types";
import { useLicenseLeasesContext } from "@/context/license-leases/useLicenseLeasesContext";
import { useSignPersonalMessage } from "@/hooks/dappkit";
import { useDynamicRewards } from "@/hooks/useDynamicRewards";
import { useNetworkStats } from "@/hooks/useNetworkStats";
import { useRewardsInfo } from "@/hooks/useRewardsInfo";
import currency from "@/lib/currency";
import dateUtils from "@/lib/date";
import { toLeaseDuration } from "@/lib/lease-duration";
import { Button } from "@/ui/nextui/Button/Button";
import { Card } from "@/ui/nextui/Card/Card";
import { Input } from "@/ui/nextui/Input/Input";
import { Tooltip } from "@/ui/nextui/Tooltip/Tooltip";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/ui/shadcn/form";
import { useToast } from "@/ui/shadcn/use-toast";

import { ClaimLeasingRewards } from "../ClaimLeasingRewards/ClaimLeasingRewards";
import { AppModal } from "../ui/AppModal";
import { LeasePerformanceChart } from "./LeasePerformanceChart";
import { LicenseLeasesProps } from "./LicenseLeases.types";

const DURATION_OPTIONS = [6, 12, 18];

// Calculate projected rewards accounting for user's license dilution
const calculateProjectedRewards = (
  licenseCount: number,
  duration: number,
  projectedReturnsPerLicense: Record<string, number>,
  totalTokensEarned: number = 0,
) => {
  const returnPerLicense = projectedReturnsPerLicense[duration.toString()] || 0;

  if (returnPerLicense === 0) {
    return { totalReward: 0, apy: 0 };
  }

  // Constants matching backend
  const dailyLeasingPool = 136986.3;
  const leaseDays = { 6: 180, 12: 365, 18: 547 };
  const days = leaseDays[duration as keyof typeof leaseDays];

  // Calculate current active licenses from the API data (reverse engineering)
  const currentDailyRewardPerLicense = returnPerLicense / days;
  const currentActiveLicenses = Math.round(dailyLeasingPool / currentDailyRewardPerLicense);

  // Calculate adjusted daily reward when user's licenses join the pool
  const totalLicensesAfterLease = currentActiveLicenses + licenseCount;
  const adjustedDailyRewardPerLicense = dailyLeasingPool / totalLicensesAfterLease;

  // Calculate total reward for user's licenses
  const totalReward = adjustedDailyRewardPerLicense * licenseCount * days;

  // Calculate APY based on projected rewards as percentage of total tokens earned
  const calculatedApy = totalTokensEarned > 0 ? (totalReward / totalTokensEarned) * 100 : 0;

  // Set minimum APY display values based on duration
  const minimumApyByDuration = {
    6: 8, // 8% minimum for 6 months
    12: 16, // 16% minimum for 12 months
    18: 32, // 32% minimum for 18 months
  };

  const minimumApy = minimumApyByDuration[duration as keyof typeof minimumApyByDuration] || 0;
  const displayApy = Math.max(calculatedApy, minimumApy);

  return {
    totalReward,
    apy: Math.max(0, displayApy),
  };
};

// Individual lease card component to use the dynamic rewards hook
const LeaseCard: React.FC<{
  lease: LeaseRecord;
}> = ({ lease }) => {
  const [calculateLeasingRewardsResponse, setCalculateLeasingRewardsResponse] = useState<
    CalculateLeasingRewardsResponse | undefined
  >(undefined);

  const { calculateLeasingRewards } = useClaimsContext();

  const isProcessing = lease.daily_rewards_claims_id != null && lease.claimed_at === null;
  const isCompleted =
    lease.status === "claimed" &&
    lease.claimed_at !== null &&
    !calculateLeasingRewardsResponse?.isClaimingEnabled;

  const currentDate = new Date();
  const isLeasePeriodExpired = !!lease.end_date && currentDate > new Date(lease.end_date);

  // Active earning state: not ready to claim, not processing, not completed
  const isActivelyEarning =
    !calculateLeasingRewardsResponse?.isClaimingEnabled && !isProcessing && !isCompleted;

  const { displayRewards, isIncrementing } = useDynamicRewards({
    totalAccumulatedRewards: lease.accumulated_leasing_rewards!,
    // The lease's real latest distribution row. Replaced a client-side guess
    // (`accumulated * 0.02`) — see the header of `hooks/useDynamicRewards.ts`.
    lastDayRewards: lease.last_daily_leasing_reward,
    lastDistributionDate: lease.last_distribution_date,
    isActive: !isLeasePeriodExpired, // Only animate if rewards are still being generated
  });

  // Helper function to format date and time on separate lines
  const formatDateTimeMultiLine = (dateString: string) => {
    const dateTimeStr = dateUtils.toFriendlyDatetimeShort(dateString);
    const parts = dateTimeStr.split(", ");

    if (parts.length === 3) {
      const datePart = parts.slice(0, 2).join(", "); // "Dec 20, 2026"
      const timePart = parts[2]; // "1:51:20 AM"

      return (
        <>
          <div>{datePart}</div>
          <div className="text-xs text-gray-300">{timePart}</div>
        </>
      );
    }

    return <div>{dateTimeStr}</div>;
  };

  // Calculate lease progress based on actual dates
  const calculateLeaseProgress = () => {
    if (lease.status === "claimed") {
      return 100;
    }

    const now = new Date();
    const startDate = new Date(lease.start_date!);
    const endDate = new Date(lease.end_date!);

    // Calculate total duration and elapsed time
    const totalDuration = endDate.getTime() - startDate.getTime();
    const elapsed = now.getTime() - startDate.getTime();

    // Calculate progress percentage (0-100)
    return Math.min(Math.max((elapsed / totalDuration) * 100, 0), 100);
  };

  // Encode lease ID to alphanumeric format (reversible)
  const encodeLeaseId = (id: number) => {
    const SECRET_KEY = 0x3b8f; // Secret XOR key
    const CHARS = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"; // Exclude confusing chars (0,O,1,I)
    const BASE_LENGTH = 6;

    // XOR with secret key and add some entropy
    let scrambled = (id ^ SECRET_KEY) * 1337 + 42069;

    scrambled = Math.abs(scrambled);

    // Convert to alphanumeric string
    let encoded = "";

    for (let i = 0; i < BASE_LENGTH; i++) {
      encoded += CHARS[scrambled % CHARS.length];
      scrambled = Math.floor(scrambled / CHARS.length);
    }

    // Add a letter prefix for extra style
    const prefixes = ["SW", "RX", "ZK", "LM", "QT", "VH", "PG", "NB"];
    const prefix = prefixes[id % prefixes.length];

    return prefix + encoded.substring(0, 4);
  };

  // Decode function (for future use if needed)
  // const decodeLeaseId = (encoded: string) => {
  //   // This would require reverse mapping - more complex with alphanumeric
  //   // Store original mapping if decode functionality is critical
  //   return null; // Simplified for now
  // };

  useEffect(() => {
    (async () => {
      const data = await calculateLeasingRewards({
        user_address: lease.owner!,
        lease_id: lease.lease_id!,
      });

      setCalculateLeasingRewardsResponse(data);
    })();
  }, []);

  // Calculate if lease is matured
  const isMatured = isLeasePeriodExpired && !isCompleted && !isProcessing;
  // The "Season Booster" badge (2026-09-27, with the seasons) and the draw-driven
  // "Maturity Boost Active" gold state (2026-09-28, with the draws) were removed.
  // Both only showed while their window was open, and neither will open again.

  // Every element inside a lease card namespaces its id with the lease id, so
  // multiple cards rendered side by side never collide.
  const cardId = `lease-card-${lease.lease_id}`;

  return (
    <>
      <div
        className={clsx("relative overflow-hidden rounded-lg border bg-black p-6 pb-16", {
          "border-gray-800":
            !calculateLeasingRewardsResponse?.isClaimingEnabled && !isCompleted && !isMatured,
          "border-green-500/50 shadow-[0_0_15px_rgba(34,197,94,0.15)]":
            !!calculateLeasingRewardsResponse?.isClaimingEnabled && !isProcessing && !isMatured,
          "border-gray-800 opacity-75": isCompleted,
        })}
        id={cardId}
      >
        {/* Header Status */}
        <div className="relative mb-6 flex items-center justify-between" id={`${cardId}-header`}>
          <div className="flex items-center gap-2" id={`${cardId}-status`}>
            {(() => {
              function statusText() {
                if (isProcessing) {
                  return "Processing";
                } else if (calculateLeasingRewardsResponse?.isClaimingEnabled && !isProcessing) {
                  return "Ready To Claim";
                } else if (isCompleted) {
                  return "Claimed";
                } else {
                  return "Active Lease";
                }
              }

              return (
                <>
                  <div
                    className={clsx("h-2 w-2 rounded-full", {
                      "animate-pulse bg-[#6CEF39]":
                        !isCompleted &&
                        !calculateLeasingRewardsResponse?.isClaimingEnabled &&
                        !isMatured,
                      "bg-[#6CEF39]":
                        !!calculateLeasingRewardsResponse?.isClaimingEnabled &&
                        !isProcessing &&
                        !isMatured,
                      "bg-gray-400": isCompleted,
                    })}
                    id={`${cardId}-status-dot`}
                  />
                  <span
                    className={clsx("text-xs font-medium uppercase tracking-wider", {
                      "text-[#6CEF39]": !isCompleted && !isMatured,
                      "text-gray-400": isCompleted,
                      "font-bold text-green-400":
                        !!calculateLeasingRewardsResponse?.isClaimingEnabled &&
                        !isProcessing &&
                        !isMatured,
                    })}
                    id={`${cardId}-status-text`}
                  >
                    {statusText()}
                  </span>
                </>
              );
            })()}
          </div>
          <span className="font-mono text-xs text-gray-500" id={`${cardId}-public-id`}>
            #{encodeLeaseId(lease.lease_id!)}
          </span>
        </div>

        {/* Passport-style Information Sections */}
        <div className="space-y-4" id={`${cardId}-details`}>
          {/* Lease Duration */}
          <div
            className="flex justify-between border-b border-gray-800 pb-2"
            id={`${cardId}-duration-row`}
          >
            <span className="text-sm text-gray-400" id={`${cardId}-duration-label`}>
              Lease Duration:
            </span>
            <span className="font-semibold text-white" id={`${cardId}-duration-value`}>
              {lease.duration_months} months
            </span>
          </div>

          {/* Leased To - Always visible */}
          <div
            className="flex justify-between border-b border-gray-800 pb-2"
            id={`${cardId}-lessee-row`}
          >
            <span className="text-sm text-gray-400" id={`${cardId}-lessee-label`}>
              Leased to:
            </span>
            <span
              className="font-mono text-sm font-semibold text-[#6CEF39]"
              id={`${cardId}-lessee-value`}
            >
              swarm@Rollup_News
            </span>
          </div>

          {/* Number of Licenses */}
          <div
            className="flex justify-between border-b border-gray-800 pb-2"
            id={`${cardId}-licenses-row`}
          >
            <span className="text-sm text-gray-400" id={`${cardId}-licenses-label`}>
              Licenses Leased:
            </span>
            <span className="font-semibold text-white" id={`${cardId}-licenses-value`}>
              {lease.license_ids?.length} License
              {lease.license_ids?.length !== 1 ? "s" : ""}
            </span>
          </div>

          {/* Rewards Section */}
          <div
            className={clsx("rounded-lg p-6", {
              "bg-gray-800/30": !isCompleted,
              "border border-gray-800 bg-gray-900/50": isCompleted,
            })}
            id={`${cardId}-rewards-section`}
          >
            <div className="mb-4 text-center" id={`${cardId}-rewards-block`}>
              <div
                className="mb-2 flex items-center justify-center gap-2"
                id={`${cardId}-rewards-heading`}
              >
                {isActivelyEarning && (
                  <>
                    <span
                      className="text-sm text-gray-400"
                      id={`${cardId}-rewards-heading-live-label`}
                    >
                      Total Rewards so far
                    </span>
                    <span
                      className="text-xs text-[#6CEF39]"
                      id={`${cardId}-rewards-live-indicator`}
                    >
                      Live Updates...
                    </span>
                  </>
                )}
                {!isActivelyEarning && (
                  <span
                    className="text-sm text-gray-400"
                    id={`${cardId}-rewards-heading-final-label`}
                  >
                    Final Reward Amount
                  </span>
                )}
              </div>

              {/* Animated Number Display */}
              <div className="relative overflow-hidden" id={`${cardId}-rewards-counter-wrapper`}>
                <div
                  className={clsx(
                    "mx-auto min-w-[120px] max-w-full px-2 py-3 sm:min-w-[200px] sm:px-4",
                    "font-offbit-bold text-center text-white",
                    "transition-all duration-500 ease-out",
                  )}
                  id={`${cardId}-rewards-counter`}
                >
                  <div
                    className={clsx(
                      "transition-all duration-300 ease-out",
                      isIncrementing && "drop-shadow-[0_0_10px_rgba(108,239,57,0.5)]",
                      isCompleted && "text-gray-300",
                    )}
                    id={`${cardId}-rewards-counter-value`}
                    style={{
                      color: isIncrementing ? "#6CEF39" : undefined,
                    }}
                  >
                    {isActivelyEarning ? (
                      <Roller
                        align="center"
                        diff={true}
                        fontSize={24}
                        rollDuration={0.6}
                        rollWay="up"
                        value={parseFloat(displayRewards.toFixed(2))}
                      />
                    ) : (
                      <span
                        className="font-offbit-bold text-[24px]"
                        id={`${cardId}-rewards-static-value`}
                      >
                        {currency.convert.toFormattedDigits(displayRewards || 0)}
                      </span>
                    )}
                  </div>

                  {/* Increment indicator */}
                  {isIncrementing && (
                    <div
                      className="absolute -right-1 -top-1 animate-bounce text-xs text-[#6CEF39]"
                      id={`${cardId}-rewards-increment-indicator`}
                    >
                      +
                    </div>
                  )}

                  {/* Subtle indicator bar */}
                  <div
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-600"
                    id={`${cardId}-rewards-counter-underline`}
                  />
                </div>
              </div>

              {/* Total estimated rewards label */}
              {isActivelyEarning ? (
                <>
                  <div
                    className="mt-2 text-xs uppercase tracking-wider text-gray-500"
                    id={`${cardId}-rewards-estimate-label`}
                  >
                    Total estimated rewards:
                  </div>
                  <div className="text-xs text-gray-500" id={`${cardId}-rewards-estimate-value`}>
                    {lease.leasing_rewards_estimate
                      ? currency.convert.toFormattedDigits(lease.leasing_rewards_estimate)
                      : "calculating..."}
                  </div>
                </>
              ) : (
                <div className="mt-2 text-xs text-gray-500" id={`${cardId}-rewards-generated-note`}>
                  {/* Keep the line but just simplified content or empty distinct space if desired,
                      user said "write something under it".
                      I put "Final Reward Amount" above, maybe put "Rewards Secured" here?
                      Actually user said "under the counter... write something".
                      I put "Final Reward Amount" ABOVE the counter in the previous block.
                      Let's move it BELOW to match user request better?
                      User: "under the counter ... under it write something"
                  */}
                  Rewards Successfully Generated
                </div>
              )}
            </div>
          </div>

          {/* Dates Section */}
          <div className="grid grid-cols-2 gap-4" id={`${cardId}-dates`}>
            <div id={`${cardId}-start-date`}>
              <span className="text-xs text-gray-400" id={`${cardId}-start-date-label`}>
                Start Date:
              </span>
              <div
                className="font-offbit-regular text-sm font-medium text-white"
                id={`${cardId}-start-date-value`}
              >
                {formatDateTimeMultiLine(lease.start_date!)}
              </div>
            </div>
            <div className="text-right" id={`${cardId}-end-date`}>
              <span className="text-xs text-gray-400" id={`${cardId}-end-date-label`}>
                End Date:
              </span>
              <div
                className="font-offbit-regular text-sm font-medium text-white"
                id={`${cardId}-end-date-value`}
              >
                {formatDateTimeMultiLine(lease.end_date!)}
              </div>
            </div>
          </div>

          {/* Lock Status / Final Status */}
          <div
            className={clsx("rounded-lg border border-gray-800 bg-gray-900/40 p-3", {
              // Optional: subtle border color change if really needed, but keeping it uniform is safer for "same style"
              "border-gray-800": true,
            })}
            id={`${cardId}-lock-status`}
          >
            <div className="flex items-center justify-between" id={`${cardId}-lock-status-row`}>
              <div className="flex items-center gap-2" id={`${cardId}-lock-status-label-group`}>
                {isActivelyEarning ? (
                  <svg
                    className="h-4 w-4 text-gray-500"
                    fill="none"
                    id={`${cardId}-lock-status-locked-icon`}
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <rect height="11" rx="2" ry="2" width="18" x="3" y="11" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                ) : isCompleted ? (
                  <svg
                    className="h-4 w-4 text-green-500"
                    fill="none"
                    id={`${cardId}-lock-status-claimed-icon`}
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M5 13l4 4L19 7"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                    />
                  </svg>
                ) : (
                  <svg
                    className="h-4 w-4 text-[#6CEF39]"
                    fill="none"
                    id={`${cardId}-lock-status-unlocked-icon`}
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                    />
                    {/* Open lock icon approximation or we can modify to be explicitly open */}
                    <path d="M7 11V7a5 5 0 0 1 9.9-1" />
                  </svg>
                )}

                <span
                  className={clsx("text-sm font-medium", {
                    "text-gray-400": isActivelyEarning,
                    "text-white": isCompleted,
                    "text-[#6CEF39]": !isActivelyEarning && !isCompleted,
                  })}
                  id={`${cardId}-lock-status-text`}
                >
                  {isActivelyEarning
                    ? "Rewards Locked"
                    : isCompleted
                      ? "Rewards Claimed"
                      : "Rewards Unlocked"}
                </span>
              </div>

              <span
                className={clsx("text-xs", {
                  "text-gray-500": isActivelyEarning,
                  "text-gray-400": isCompleted,
                  "text-[#6CEF39]": !isActivelyEarning && !isCompleted,
                })}
                id={`${cardId}-lock-status-detail`}
              >
                {isActivelyEarning
                  ? `${lease.days_remaining}d remaining`
                  : isCompleted
                    ? `${lease.claimed_at ? dateUtils.toFriendlyDate(lease.claimed_at) : "Unknown"}`
                    : "Ready to claim"}
              </span>
            </div>
          </div>
        </div>

        {/* Progress Bar or Completion Status */}
        {isActivelyEarning ? (
          <div className="mt-6" id={`${cardId}-progress`}>
            <div
              className="mb-2 flex justify-between text-xs text-gray-400"
              id={`${cardId}-progress-heading`}
            >
              <span id={`${cardId}-progress-label`}>Lease Progress</span>
              <span id={`${cardId}-progress-percent`}>{Math.round(calculateLeaseProgress())}%</span>
            </div>
            <div className="flex gap-1" id={`${cardId}-progress-bar`}>
              {Array.from(Array(20).keys()).map((_, i) => {
                const progress = calculateLeaseProgress();
                const segmentSize = 5; // Each segment represents 5%
                const completedSegments = Math.floor(progress / segmentSize);
                const hasPartialSegment = progress % segmentSize > 0;
                const isFlashingSegment = hasPartialSegment && i === completedSegments;
                const isSolidSegment = i < completedSegments;

                return (
                  <div
                    key={`progress-${i}`}
                    className={clsx("h-3 flex-1 bg-gray-800", {
                      "!bg-green-500": isSolidSegment,
                      "animate-pulse !bg-green-500": isFlashingSegment,
                    })}
                    id={`${cardId}-progress-segment-${i}`}
                  />
                );
              })}
            </div>
          </div>
        ) : (
          <div className="mt-6" id={`${cardId}-completed-banner-wrapper`}>
            <div
              className={clsx(
                "flex items-center justify-center rounded border border-gray-800 bg-gray-900/60 py-3 text-xs font-medium uppercase tracking-wider",
                {
                  "text-gray-400": isCompleted,
                  "border-green-500/30 bg-green-500/10 text-[#6CEF39]": !isCompleted, // Ready to claim
                },
              )}
              id={`${cardId}-completed-banner`}
            >
              {isCompleted ? "Lease Period Completed" : "Lease Period Completed"}
            </div>
          </div>
        )}

        {/* Action Button */}
        <div className="absolute bottom-0 left-0 right-0" id={`${cardId}-action-bar`}>
          {!!calculateLeasingRewardsResponse?.isClaimingEnabled && !isProcessing ? (
            <ClaimLeasingRewards
              calculateLeasingRewardsResponse={calculateLeasingRewardsResponse}
              leaseId={lease.lease_id!}
            />
          ) : isCompleted ? (
            <div
              className="flex h-12 w-full items-center justify-center border-t border-gray-800 bg-gray-900 text-sm font-medium text-gray-500"
              id={`${cardId}-cycle-completed-banner`}
            >
              Lease Cycle Completed
            </div>
          ) : (
            <Button
              disabled
              fullWidth
              className="rounded-none text-gray-400"
              color="default"
              id={`${cardId}-claim-disabled-button`}
              size="lg"
              type="button"
            >
              {isProcessing
                ? "Processing..."
                : `Claim Rewards (${lease.days_remaining || 0} days left)`}
            </Button>
          )}
        </div>
      </div>
    </>
  );
};

export const LicenseLeases: React.FC<LicenseLeasesProps> = ({ onClickToggleBuyNowModal }) => {
  const account = useCurrentAccount();
  // v2: `useCurrentWallet()` returns the wallet only — connection flags moved to
  // `useWalletConnection()`.
  const wallet = useWalletConnection();

  const {
    getLicensesByOwner,
    getLeasesByOwner,
    licensesByOwner,
    leasesByOwner,
    requestNewLease,
    requestNewLeaseForm,
    actions,
  } = useLicenseLeasesContext();

  const { mutate: signPersonalMessage } = useSignPersonalMessage();
  const { networkStats, isLoading: isNetworkStatsLoading } = useNetworkStats();
  const { data: rewardsData } = useRewardsInfo(account?.address);
  const { generateNonce, isGeneratingNonce } = useAuthenticationContext();

  const { toast } = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const formValues = requestNewLeaseForm.watch();

  // Calculate projected rewards using the simplified backend data
  const projectedRewards = React.useMemo(() => {
    if (!networkStats) return { totalReward: 0, apy: 0 };

    return calculateProjectedRewards(
      Number(formValues.quantity) || 0,
      Number(formValues.duration) || 6,
      networkStats.projected_returns_per_license,
      rewardsData?.totalTokens || 0,
    );
  }, [formValues.quantity, formValues.duration, networkStats, rewardsData?.totalTokens]);

  const activeLeases = licensesByOwner?.license_leases_by_duration_period || {};
  const activeLeasePeriods = Object.keys(activeLeases);

  // State to track chart data for total rewards calculation
  const [chartData, setChartData] = useState<LeaseDailyRewardPoint[]>([]);

  function onOpenLeaseModal(_values: z.infer<typeof RequestNewLeaseFormSchema>) {
    // Open the confirmation modal instead of directly submitting
    onOpen();
  }

  async function onConfirmLease() {
    if (!account?.address) return;

    try {
      // Step 1: Generate nonce
      const nonceData = await generateNonce(account.address);

      if (!nonceData) {
        toast({
          title: "Error",
          description: "Failed to generate security nonce. Please try again.",
          variant: "destructive",
        });

        return;
      }

      const values = requestNewLeaseForm.getValues();
      const { quantity, duration } = values;

      // Step 2: Create message with nonce and timestamp
      const message = new TextEncoder().encode(
        `I agree to lease ${quantity} license(s) for ${duration} months. I understand that this action is irreversible and my licenses will be locked for the entire duration. I also acknowledge that the rewards are subject to network performance. nonce: ${nonceData.nonce}`,
      );

      // Step 3: Sign the message
      signPersonalMessage(
        {
          message,
        },
        {
          onSuccess(data) {
            requestNewLease({
              owner: account.address,
              message: Array.from(message),
              signature: data.signature,
              nonce: nonceData.nonce,
              nonceToken: nonceData.nonceToken,
              duration_months: toLeaseDuration(values.duration),
              quantity: Number(values.quantity),
            });

            onClose(); // Close modal after successful submission
          },
          onError(error) {
            toast({
              title: "Error",
              description: <p className="mb-0">While submitting your signature: {error.message}</p>,
              variant: "destructive",
            });
          },
        },
      );
    } catch {
      toast({
        title: "Error",
        description: "Failed to process lease request. Please try again.",
        variant: "destructive",
      });
    }
  }

  useEffect(() => {
    if (!wallet.isConnected || !account?.address) return;

    const owner = account.address;

    getLicensesByOwner(owner);
    getLeasesByOwner(owner);
  }, [wallet.isConnected, account?.address]);

  // Set default quantity to maximum available licenses when data is loaded
  useEffect(() => {
    if (licensesByOwner?.unleased_licenses && licensesByOwner.unleased_licenses.length > 0) {
      requestNewLeaseForm.setValue("quantity", String(licensesByOwner.unleased_licenses.length), {
        shouldValidate: true,
        shouldTouch: false, // Don't mark as touched since this is a default value
      });
    }
  }, [licensesByOwner?.unleased_licenses, requestNewLeaseForm]);

  return (
    <>
      <div className="mb-8" id="license-leasing-heading">
        <h4 className="uppercase" id="license-leasing-title">
          License Leasing
        </h4>
      </div>

      <div className="mb-8 grid grid-cols-1 gap-8 lg:grid-cols-2" id="license-leasing-top-row">
        <div className="lg:col-span-1" id="lease-portal-col">
          <Form {...requestNewLeaseForm}>
            <form
              className="mb-4 h-full sm:mb-0"
              id="request-new-lease-form"
              onSubmit={requestNewLeaseForm.handleSubmit(onOpenLeaseModal)}
            >
              <Card
                className="relative h-full max-h-[375px] min-h-[375px] border-1 border-gray-800 bg-background pb-0"
                id="lease-portal-card"
              >
                <Card.Header id="lease-portal-card-header">
                  <h4 className="uppercase" id="lease-portal-card-title">
                    Lease Portal
                  </h4>
                </Card.Header>
                <Card.Body className="flex flex-col pb-20 pt-4" id="lease-portal-card-body">
                  {licensesByOwner?.unleased_licenses &&
                  licensesByOwner?.unleased_licenses.length > 0 ? (
                    <div
                      className="grid grid-cols-1 gap-8 md:grid-cols-2"
                      id="lease-portal-form-grid"
                    >
                      <div className="flex flex-col gap-8" id="lease-portal-inputs">
                        <FormField
                          control={requestNewLeaseForm.control}
                          name="quantity"
                          render={({ field }) => (
                            <FormItem id="request-new-lease-quantity-item">
                              <div
                                className="flex justify-between"
                                id="request-new-lease-quantity-label-row"
                              >
                                <FormLabel id="request-new-lease-quantity-label">
                                  Quantity
                                </FormLabel>
                                <Button
                                  className="h-auto p-0 text-xs"
                                  id="request-new-lease-quantity-max-button"
                                  size="sm"
                                  variant="light"
                                  onPress={() => {
                                    requestNewLeaseForm.setValue(
                                      "quantity",
                                      String(licensesByOwner.unleased_licenses.length),
                                      {
                                        shouldValidate: true,
                                        shouldTouch: true,
                                      },
                                    );
                                  }}
                                >
                                  Max: {licensesByOwner.unleased_licenses.length}
                                </Button>
                              </div>
                              <FormControl>
                                <Input
                                  classNames={{
                                    inputWrapper: "bg-black border-2 border-gray-700",
                                    input: "bg-transparent text-white",
                                  }}
                                  endContent={
                                    <Button
                                      isIconOnly
                                      className="text-2xl"
                                      id="request-new-lease-quantity-increment-button"
                                      type="button"
                                      variant="light"
                                      onClick={() => {
                                        if (
                                          Number(field.value) >=
                                          licensesByOwner.unleased_licenses.length
                                        )
                                          return;

                                        requestNewLeaseForm.setValue(
                                          field.name,
                                          String(Number(field.value) + 1),
                                          {
                                            shouldTouch: true,
                                            shouldValidate: true,
                                          },
                                        );
                                      }}
                                    >
                                      +
                                    </Button>
                                  }
                                  placeholder="1"
                                  size="lg"
                                  startContent={
                                    <Button
                                      isIconOnly
                                      className="text-2xl"
                                      id="request-new-lease-quantity-decrement-button"
                                      type="button"
                                      variant="light"
                                      onClick={() => {
                                        if (Number(field.value) === 1) return;

                                        requestNewLeaseForm.setValue(
                                          field.name,
                                          String(Number(field.value) - 1),
                                          {
                                            shouldTouch: true,
                                            shouldValidate: true,
                                          },
                                        );
                                      }}
                                    >
                                      -
                                    </Button>
                                  }
                                  type="text"
                                  value={field.value}
                                  onChange={(e) => {
                                    const { value } = e.target;
                                    const maxQuantity =
                                      licensesByOwner?.unleased_licenses.length || 1;

                                    // Allow empty string to clear input, but handle validation via schema
                                    if (value === "") {
                                      field.onChange("");

                                      return;
                                    }

                                    // Use regex to allow only integer values
                                    if (!/^\d*$/.test(value)) {
                                      return; // Exit if not a valid integer string
                                    }

                                    const numValue = parseInt(value, 10);

                                    // Prevent values greater than max available licenses
                                    if (numValue > maxQuantity) {
                                      field.onChange(String(maxQuantity));

                                      return;
                                    }

                                    // Update form state with valid integer value
                                    field.onChange(value);
                                  }}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={requestNewLeaseForm.control}
                          name="duration"
                          render={({ field }) => (
                            <FormItem id="request-new-lease-duration-item">
                              <FormLabel id="request-new-lease-duration-label">Duration</FormLabel>
                              <FormControl>
                                <div
                                  className="grid grid-cols-3 gap-2"
                                  id="request-new-lease-duration-options"
                                >
                                  {DURATION_OPTIONS.map((duration) => (
                                    <Button
                                      key={duration}
                                      color={
                                        Number(field.value) === duration ? "success" : "default"
                                      }
                                      id={`request-new-lease-duration-option-${duration}`}
                                      size="lg"
                                      type="button"
                                      variant={
                                        Number(field.value) === duration ? "solid" : "bordered"
                                      }
                                      onPress={() => {
                                        field.onChange(String(duration));
                                      }}
                                    >
                                      {duration} months
                                    </Button>
                                  ))}
                                </div>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      <div
                        className="flex flex-col items-center justify-center rounded-lg border-l-1 border-gray-800 p-4 pl-8 text-center"
                        id="lease-portal-projection-panel"
                      >
                        <div
                          className="flex items-center justify-center gap-2"
                          id="lease-portal-projected-lr-heading"
                        >
                          <span
                            className="text-sm text-gray-500"
                            id="lease-portal-projected-lr-label"
                          >
                            Projected Potential LR
                          </span>
                          <Tooltip content="This is your estimated potential return based on projected rewards as a percentage of your total tokens earned. The calculation uses current network data and may change.">
                            <div
                              className="flex items-center"
                              id="lease-portal-projected-lr-tooltip-trigger"
                            >
                              <QuestionMarkCircledIcon className="-mt-0.5 h-3.5 w-3.5 text-yellow-300" />
                            </div>
                          </Tooltip>
                        </div>

                        {isNetworkStatsLoading ? (
                          <div
                            className="my-2 flex items-center justify-center"
                            id="lease-portal-projected-lr-loading"
                          >
                            <div
                              className="h-8 w-8 animate-spin rounded-full border-b-2 border-green-500"
                              id="lease-portal-projected-lr-spinner"
                            />
                          </div>
                        ) : (
                          <p
                            className="my-2 text-5xl font-bold text-green-500"
                            id="lease-portal-projected-lr-value"
                          >
                            {Math.round(projectedRewards.apy)}%
                          </p>
                        )}

                        <p
                          className="mt-4 text-sm text-gray-500"
                          id="lease-portal-estimated-rewards-label"
                        >
                          Total Estimated Rewards
                        </p>

                        {isNetworkStatsLoading ? (
                          <div
                            className="flex items-center justify-center"
                            id="lease-portal-estimated-rewards-loading"
                          >
                            <div
                              className="h-6 w-32 animate-pulse rounded bg-gray-600"
                              id="lease-portal-estimated-rewards-skeleton"
                            />
                          </div>
                        ) : (
                          <p className="text-xl" id="lease-portal-estimated-rewards-value">
                            ~{currency.convert.toFormattedDigits(projectedRewards.totalReward)}{" "}
                          </p>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div
                      className="flex flex-col justify-center px-6 py-2"
                      id="lease-portal-empty-state"
                    >
                      <div
                        className="space-y-3 text-sm leading-relaxed text-gray-300"
                        id="lease-portal-empty-state-copy"
                      >
                        <p id="lease-portal-empty-state-intro">
                          Lease your licenses to the Swarm Network and earn rewards based on the
                          duration and quantity of your lease.
                        </p>
                        <p id="lease-portal-empty-state-products">
                          Swarm utilizes your licenses to power and enhance products like{" "}
                          <span
                            className="font-medium text-[#6CEF39]"
                            id="lease-portal-empty-state-rollup-news"
                          >
                            Rollup News
                          </span>{" "}
                          and other network services, generating value that&apos;s shared back with
                          license holders.
                        </p>
                        <p id="lease-portal-empty-state-scaling">
                          <span
                            className="font-medium text-white"
                            id="lease-portal-empty-state-scaling-label"
                          >
                            Rewards scale with:
                          </span>{" "}
                          lease duration and number of licenses.
                        </p>
                        <p
                          className="text-xs italic text-gray-400"
                          id="lease-portal-empty-state-cta"
                        >
                          Purchase licenses below to start earning rewards through the leasing
                          program.
                        </p>
                      </div>
                    </div>
                  )}
                </Card.Body>
                <Card.Footer
                  className="absolute bottom-0 left-0 right-0 block overflow-visible px-0 pb-0"
                  id="lease-portal-card-footer"
                >
                  <div className="mx-0" id="lease-portal-submit-wrapper">
                    {!licensesByOwner ? (
                      <Button
                        disabled
                        fullWidth
                        className="rounded-none"
                        color="default"
                        id="lease-portal-loading-button"
                        size="lg"
                        type="button"
                      >
                        Loading...
                      </Button>
                    ) : licensesByOwner?.unleased_licenses &&
                      licensesByOwner?.unleased_licenses.length === 0 ? (
                      <Button
                        fullWidth
                        className="rounded-none"
                        color="success"
                        id="lease-portal-buy-now-button"
                        size="lg"
                        type="button"
                        onPress={onClickToggleBuyNowModal}
                      >
                        No license left to lease, buy now
                      </Button>
                    ) : actions.requestNewLease.isLoading ? (
                      <Button
                        disabled
                        fullWidth
                        className="rounded-none"
                        color="success"
                        id="lease-portal-requesting-button"
                        size="lg"
                        type="button"
                      >
                        Requesting...
                      </Button>
                    ) : (
                      <Button
                        fullWidth
                        className="rounded-none"
                        color="success"
                        disabled={!requestNewLeaseForm.formState.isValid}
                        id="lease-portal-submit-button"
                        size="lg"
                        type="submit"
                      >
                        Lease {formValues.quantity} Licenses for {formValues.duration} Months
                      </Button>
                    )}
                  </div>
                </Card.Footer>
              </Card>
            </form>
          </Form>
        </div>

        <div className="lg:col-span-1" id="lease-performance-col">
          {activeLeasePeriods.length > 0 && account?.address && (
            <Card
              className="h-full max-h-[500px] border-1 border-gray-800 bg-background"
              id="lease-performance-card"
            >
              <Card.Header id="lease-performance-card-header">
                <div className="flex items-start justify-between" id="lease-performance-header-row">
                  <div id="lease-performance-header-text">
                    <h4 className="mr-4 uppercase" id="lease-performance-card-title">
                      Lease Performance
                    </h4>
                    {chartData.length > 0 && (
                      <p className="mt-1 text-xs text-gray-500" id="lease-performance-as-of-date">
                        as of{" "}
                        {dateUtils.toFriendlyDatetimeShort(chartData[chartData.length - 1]?.date)}
                      </p>
                    )}
                  </div>
                  {chartData.length > 0 && (
                    <div className="text-right" id="lease-performance-total-wrapper">
                      <p
                        className="text-2xl font-bold leading-tight text-green-500"
                        id="lease-performance-total-value"
                      >
                        {currency.convert.toFormattedDigits(
                          chartData[chartData.length - 1]?.accumulative_reward || 0,
                        )}
                      </p>
                    </div>
                  )}
                </div>
              </Card.Header>
              <Card.Body className="overflow-hidden" id="lease-performance-card-body">
                <LeasePerformanceChart
                  hasActiveLeases={activeLeasePeriods.length > 0}
                  ownerAddress={account.address}
                  onDataChange={setChartData}
                />
              </Card.Body>
            </Card>
          )}
        </div>
      </div>

      {!!leasesByOwner?.leases && leasesByOwner?.leases.length > 0 && (
        <>
          <div className="mb-8" id="leases-list-heading">
            <h4 className="uppercase" id="leases-list-title">
              Leases
            </h4>
          </div>
          <div
            className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
            id="leases-list-grid"
          >
            {leasesByOwner.leases
              .sort((a, b) => {
                // Priority 1: Ready to claim (is_claimable === true)
                if (a.is_claimable && !b.is_claimable) return -1;
                if (!a.is_claimable && b.is_claimable) return 1;

                // Priority 2: Claimed (status === "claimed") - Move to bottom
                if (a.status === "claimed" && b.status !== "claimed") return 1;
                if (a.status !== "claimed" && b.status === "claimed") return -1;

                // Priority 3: Default sort by end date (sooner ending first)
                if (a.end_date && b.end_date) {
                  return new Date(a.end_date).getTime() - new Date(b.end_date).getTime();
                }

                return 0;
              })
              .map((lease) => {
                return <LeaseCard key={lease.lease_id} lease={lease} />;
              })}
          </div>
        </>
      )}

      {/* Lease Confirmation Modal */}
      <AppModal
        footer={
          <>
            <Button
              color="danger"
              id="confirm-lease-modal-cancel-button"
              variant="light"
              onPress={onClose}
            >
              Cancel
            </Button>
            <Button
              color="success"
              id="confirm-lease-modal-confirm-button"
              isLoading={actions.requestNewLease.isLoading || isGeneratingNonce}
              onPress={onConfirmLease}
            >
              {isGeneratingNonce
                ? "Generating Security Token..."
                : actions.requestNewLease.isLoading
                  ? "Confirming..."
                  : "Confirm Lease"}
            </Button>
          </>
        }
        id="confirm-lease-modal"
        isOpen={isOpen}
        size="2xl"
        subtitle="Please review the lease details before confirming"
        title="Confirm License Lease"
        onClose={onClose}
      >
        {(() => {
          const duration = Number(formValues.duration) || 6;
          const quantity = Number(formValues.quantity) || 1;
          const daysInLease = { 6: 180, 12: 365, 18: 547 }[duration] || 180;

          // Calculate release date
          const releaseDate = new Date();

          releaseDate.setDate(releaseDate.getDate() + daysInLease);
          const formattedReleaseDate = releaseDate.toLocaleDateString();

          // Calculate potential earnings
          const potentialEarnings = projectedRewards.totalReward;

          return (
            <div className="space-y-6" id="confirm-lease-modal-content">
              {/* Important Notice */}
              <div
                className="rounded-lg border border-gray-700/50 bg-gray-800/30 p-4"
                id="confirm-lease-modal-notice"
              >
                <div className="flex items-start gap-3" id="confirm-lease-modal-notice-row">
                  <div className="mt-0.5 text-blue-400" id="confirm-lease-modal-notice-icon">
                    <svg
                      className="h-4 w-4"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
                    </svg>
                  </div>
                  <div id="confirm-lease-modal-notice-text">
                    <h4
                      className="mb-1 font-semibold text-gray-200"
                      id="confirm-lease-modal-notice-title"
                    >
                      Lease Details
                    </h4>
                    <p className="text-sm text-gray-300" id="confirm-lease-modal-notice-body">
                      When leasing, your {quantity} license
                      {quantity > 1 ? "s" : ""}, any accumulated rewards starting from the start of
                      the lease period including rewards that come from the daily operation pool
                      will be locked for the entire lease period and cannot be accessed until the
                      lease expires.
                    </p>
                  </div>
                </div>
              </div>

              {/* Lease Details */}
              <div className="space-y-4" id="confirm-lease-modal-details">
                <div className="grid grid-cols-2 gap-4" id="confirm-lease-modal-summary-grid">
                  <div className="space-y-2" id="confirm-lease-modal-duration-stat">
                    <h4
                      className="font-semibold text-white"
                      id="confirm-lease-modal-duration-label"
                    >
                      Lease Duration
                    </h4>
                    <p
                      className="text-2xl font-bold text-[#6CEF39]"
                      id="confirm-lease-modal-duration-value"
                    >
                      {duration} months
                    </p>
                    <p className="text-sm text-gray-400" id="confirm-lease-modal-duration-days">
                      ({daysInLease} days)
                    </p>
                  </div>

                  <div className="space-y-2" id="confirm-lease-modal-quantity-stat">
                    <h4
                      className="font-semibold text-white"
                      id="confirm-lease-modal-quantity-label"
                    >
                      Licenses to Lease
                    </h4>
                    <p
                      className="text-2xl font-bold text-[#6CEF39]"
                      id="confirm-lease-modal-quantity-value"
                    >
                      {quantity} license{quantity > 1 ? "s" : ""}
                    </p>
                  </div>
                </div>

                <div className="border-t border-gray-700 pt-4" id="confirm-lease-modal-lock-period">
                  <h4
                    className="mb-2 font-semibold text-white"
                    id="confirm-lease-modal-lock-period-label"
                  >
                    Lock Period
                  </h4>
                  <p className="text-gray-300" id="confirm-lease-modal-lock-period-text">
                    Your licenses will be locked until{" "}
                    <span
                      className="font-semibold text-[#6CEF39]"
                      id="confirm-lease-modal-release-date"
                    >
                      {formattedReleaseDate}
                    </span>
                  </p>
                </div>

                <div className="border-t border-gray-700 pt-4" id="confirm-lease-modal-earnings">
                  <h4
                    className="mb-2 font-semibold text-white"
                    id="confirm-lease-modal-earnings-label"
                  >
                    Potential Earnings
                  </h4>
                  <p
                    className="text-3xl font-bold text-[#6CEF39]"
                    id="confirm-lease-modal-earnings-value"
                  >
                    ~{currency.convert.toFormattedDigits(potentialEarnings)}
                  </p>
                  <p className="mt-1 text-sm text-gray-400" id="confirm-lease-modal-earnings-hint">
                    Estimated total rewards for the lease period
                  </p>

                  {projectedRewards.apy > 0 && (
                    <p className="mt-2 text-sm text-gray-400" id="confirm-lease-modal-projected-lr">
                      Projected LR:{" "}
                      <span
                        className="font-semibold text-[#6CEF39]"
                        id="confirm-lease-modal-projected-lr-value"
                      >
                        {Math.round(projectedRewards.apy)}%
                      </span>
                    </p>
                  )}
                </div>

                <div className="border-t border-gray-700 pt-4" id="confirm-lease-modal-terms">
                  <h4
                    className="mb-2 font-semibold text-white"
                    id="confirm-lease-modal-terms-label"
                  >
                    Lease Terms
                  </h4>
                  <ul
                    className="space-y-1 text-sm text-gray-300"
                    id="confirm-lease-modal-terms-list"
                  >
                    <li id="confirm-lease-modal-terms-item-usage">
                      • Your licenses will be used to power Swarm Network services
                    </li>
                    <li id="confirm-lease-modal-terms-item-distribution">
                      • Rewards are distributed daily based on network activity
                    </li>
                    <li id="confirm-lease-modal-terms-item-lock">
                      • You cannot access or transfer leased licenses until expiration
                    </li>
                    <li id="confirm-lease-modal-terms-item-claim">
                      • Rewards can only be claimed after the lease period ends
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          );
        })()}
      </AppModal>
    </>
  );
};
